import type { DocusignSendProgressUpdateDto } from '../services/contracts/ContractService';

export type DocusignTimelineProgress = DocusignSendProgressUpdateDto & {
  status: 'processing' | 'success' | 'error';
};

const STAGE_LABELS: Record<string, string> = {
  queued: 'Na fila',
  fila_bot: 'Aguardando navegador',
  prioridade: 'Aguardando prioridade',
  retry: 'Reiniciando envio',
  iniciando: 'Iniciando',
  download: 'Download do contrato',
  extracao: 'Extração dos PDFs',
  bot: 'Automação DocuSign',
  upload: 'Upload do documento',
  configuracao: 'Configurando assinatura',
  whatsapp: 'Configurando WhatsApp',
  envio: 'Enviando para assinatura',
  concluido: 'Concluído',
  erro: 'Erro',
  cancelado: 'Cancelado',
  cancelando: 'Cancelando',
};

export const getDocusignStageLabel = (stage: string): string => {
  const key = (stage || '').trim().toLowerCase();
  return STAGE_LABELS[key] || stage || 'Processando';
};

/** Fase de espera na fila prioritária do RPA (RF12 / T04). */
export const isDocusignPriorityWaitPhase = (phase: {
  stage?: string;
  message?: string;
}): boolean => {
  const stage = (phase.stage || '').trim().toLowerCase();
  if (stage === 'prioridade') {
    return true;
  }
  const message = (phase.message || '').toLowerCase();
  return message.includes('prioridade') || message.includes('próxima');
};

const normalizeStatus = (status?: string): DocusignTimelineProgress['status'] => {
  if (status === 'success' || status === 'error') {
    return status;
  }
  return 'processing';
};

export const mapPollItemToTimelineProgress = (
  contractId: string,
  item: DocusignSendProgressUpdateDto
): DocusignTimelineProgress => ({
  contractId: item.contractId || contractId,
  stage: item.stage || 'processando',
  progress: item.progress ?? 0,
  message: item.message || 'Processando envio DocuSign...',
  status: normalizeStatus(item.status),
  timestamp: item.timestamp,
  preserveProgress: item.preserveProgress,
  resetProgress: item.resetProgress,
  isQueued: item.isQueued,
  queuePosition: item.queuePosition,
  queueTotal: item.queueTotal,
});

/** Remove entradas consecutivas com mesmo stage e message. */
export const dedupeConsecutivePhases = (
  items: DocusignTimelineProgress[]
): DocusignTimelineProgress[] => {
  const result: DocusignTimelineProgress[] = [];
  for (const item of items) {
    const prev = result[result.length - 1];
    if (
      prev &&
      prev.stage === item.stage &&
      prev.message === item.message &&
      prev.status === item.status
    ) {
      continue;
    }
    result.push(item);
  }
  return result;
};

/** Mais recente primeiro (para exibição na modal). */
export const sortPhasesNewestFirst = (
  items: DocusignTimelineProgress[]
): DocusignTimelineProgress[] => {
  return [...items].sort((a, b) => {
    const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    if (ta !== tb) {
      return tb - ta;
    }
    return 0;
  });
};

export const buildTimelineFromPollResponse = (
  contractId: string,
  progress: DocusignSendProgressUpdateDto[] | undefined,
  currentProgress: DocusignSendProgressUpdateDto | null | undefined
): DocusignTimelineProgress[] => {
  const all: DocusignTimelineProgress[] = [];

  const push = (item: DocusignSendProgressUpdateDto | null | undefined) => {
    if (!item) return;
    const stageKey = (item.stage || '').trim().toLowerCase();
    if (stageKey === 'localizacao') return;
    all.push(mapPollItemToTimelineProgress(contractId, item));
  };

  (progress ?? []).forEach(push);
  push(currentProgress);

  // Deduplicate by stage+message+status (ignoring timestamp), keeping the most recent entry.
  // This prevents the same phase from appearing twice when the RPA re-emits the same stage.
  const latestByKey = new Map<string, DocusignTimelineProgress>();
  for (const item of all) {
    const key = `${item.stage}|${item.message}|${item.status}`;
    const existing = latestByKey.get(key);
    const itemTime = item.timestamp ? new Date(item.timestamp).getTime() : 0;
    const existingTime = existing?.timestamp ? new Date(existing.timestamp).getTime() : 0;
    if (!existing || itemTime >= existingTime) {
      latestByKey.set(key, item);
    }
  }

  return sortPhasesNewestFirst(Array.from(latestByKey.values()));
};
